import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ChatMessage } from '@sr-test-wizard/shared';

interface Props {
  messages: ChatMessage[];
  busy: boolean;
  onSend: (question: string) => void;
}

/**
 * Per-bug chat box used by the Bugs review pane. Visually parallel to
 * the test-level {@link AIChat} but lives on its own JSONL transcript so a
 * single test and a bug raised against it never share history.
 */
export function BugChat({ messages, busy, onSend }: Props) {
  const [draft, setDraft] = useState('');
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight });
  }, [messages.length]);

  const send = () => {
    const q = draft.trim();
    if (!q) return;
    setDraft('');
    onSend(q);
  };

  return (
    <div className="chat" style={{ marginTop: 8 }}>
      <h4 style={{ margin: '0 0 8px' }}>Chat with AI about this bug</h4>
      <div ref={feedRef} className="messages">
        {messages.length === 0 && (
          <p className="muted small">
            The AI sees the bug summary, status, description, comments,
            attachments, the originating test review (if linked), and the
            REQs that apply to this run's jurisdictions.
          </p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`msg ${m.role}`}>
            <ReactMarkdown>{m.content}</ReactMarkdown>
          </div>
        ))}
      </div>
      <div className="composer">
        <textarea
          value={draft}
          placeholder="Is the fix verified? Do we need a new build? Any new screenshots?"
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !draft.trim()}
          onClick={send}
        >
          {busy ? 'Sending...' : 'Send'}
        </button>
      </div>
      <p className="muted small">Press Ctrl/Cmd + Enter to send.</p>
    </div>
  );
}
